import axios from 'axios';
import { toast } from 'react-hot-toast';
import { saveAs } from 'file-saver';
// ============================
// 🔧 AXIOS INSTANCES
// ============================

const chatApi = axios.create({
  baseURL: process.env.REACT_APP_CHAT_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

const contractApi = axios.create({
  baseURL: process.env.REACT_APP_CONTRACT_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});
const baseApi = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});
const subscriptionApi = axios.create({
  baseURL: process.env.REACT_APP_SUBSCRIPTION_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ✅ Handle 401 Unauthorized globally
let isLoggingOut = false;

const attachAuthErrorInterceptor = (api) => {
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      const url = error.config?.url || '';

      // 🚫 DO NOT show session-expired toast on auth routes
      const isAuthRoute =
        url.includes('/auth/signin') ||
        url.includes('/auth/signup') ||
        url.includes('/auth/social-login') ||
        url.includes('/auth/verify') ||
        url.includes('/auth/resend-otp') ||
        url.includes('/auth/forgot-password') ||
        url.includes('/auth/reset-password');

      if (isAuthRoute || url.includes('/auth/me')) {
        return Promise.reject(error);
      }

      const isAuthError =
        status === 401 ||
        (status === 403 && detail === 'Not authenticated') ||
        (status === 401 && detail === 'Unauthorized');

      if (isAuthError && !isLoggingOut) {
        isLoggingOut = true;

        toast.error('Your session has expired. Please sign in again.', {
          duration: 3000,
        });

        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('theme');

        setTimeout(() => {
          isLoggingOut = false;
          window.location.assign('/login');
        }, 1500);

        return Promise.reject({ __handledAuthError: true });
      }

      return Promise.reject(error);
    },
  );
};

const attachToken = (api) => {
  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('authToken');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
};

attachToken(baseApi);
attachToken(chatApi);
attachToken(contractApi);
attachToken(subscriptionApi);

attachAuthErrorInterceptor(baseApi);
attachAuthErrorInterceptor(chatApi);
attachAuthErrorInterceptor(contractApi);
attachAuthErrorInterceptor(subscriptionApi);

// ============================
// ⚠️ GLOBAL INTERCEPTORS
// ============================

const addErrorInterceptor = (apiInstance) => {
  apiInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;

      const isAuthError =
        status === 401 || (status === 403 && detail === 'Not authenticated');

      // 🚫 Do not log auth-related errors again
      if (isAuthError) {
        return Promise.reject(error);
      }

      console.error('API Error:', error?.response?.data || error.message);
      return Promise.reject(error);
    },
  );
};

addErrorInterceptor(chatApi);
addErrorInterceptor(contractApi);
addErrorInterceptor(subscriptionApi);

/**
 * Fetch the current authenticated session.
 * Requires a valid JWT (added automatically via interceptor).
 * @returns {Promise<Object>} Session data (user, token info, expiry, etc.)
 */
export const getAuthSession = async () => {
  try {
    const { data } = await baseApi.get('/auth/sessions');
    return data; // Expected: { success: true, session: {...} }
  } catch (error) {
    throw error;
  }
};

// ============================
// 📦 PACKAGES API
// ============================
export const getPackages = async () => {
  try {
    const { data } = await baseApi.get('/packages');
    return data;
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch packages';
    throw new Error(message);
  }
};
/**
 * Create Stripe checkout session
 * @param {string} packageId
 * @returns {Promise<{ checkout_url: string, session_id: string }>}
 */
export const createCheckoutSession = async (packageId) => {
  if (!packageId) {
    throw new Error('packageId is required');
  }

  try {
    const { data } = await subscriptionApi.post(
      '/subscriptions/create-checkout',
      {
        package_id: packageId,
      },
    );

    if (!data?.checkout_url) {
      throw new Error('Invalid checkout response');
    }

    return data;
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      'Failed to start checkout';

    throw new Error(message);
  }
};

// ============================
// 📦 STATES API
// ============================
export const getStates = async () => {
  try {
    const { data } = await baseApi.get('/states');
    return data; // Expected: [ "Alabama", "Alaska", ... ]
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch states';
    throw new Error(message);
  }
};

// ============================
// 📦 AUTH API METHODS
// ============================

/**
 * Logs in a user with email and password.
 * @param {string} email - User email.
 * @param {string} password - User password.
 * @param {string} country - User Country.
 * @returns {Promise<Object>} Response containing success flag, token, and user details.
 */
export const loginUser = async (email, password, country, session_state) => {
  try {
    const response = await baseApi.post('/auth/signin', {
      email,
      password,
      country,
      session_state,
    });
    return response.data; // Expected: { success: true, token, user }
  } catch (error) {
    console.error('loginUser error:', error?.response?.data || error.message);
    throw error;
  }
};
export const logoutSession = async (sessionId) => {
  try {
    const { data } = await baseApi.post('/auth/logout', { sessionId });
    return data; // expected: { success: true }
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        error.message ||
        'Failed to logout session',
    );
  }
};
// ============================
// 🌍 COUNTRY API
// ============================

/**
 * Get country info based on IP
 * @param {string} [ip] - Optional IP override
 * @param {string} [default_country='US'] - Fallback country
 * @returns {Promise<{ country: string, region: string, flag: string, success: boolean }>}
 */
export const getCountryInfo = async (ip = null, default_country = 'US') => {
  try {
    const { data } = await baseApi.get('/api/country', {
      params: {
        default_country,
        ip,
      },
    });

    return data;
  } catch (error) {
    console.error(
      'getCountryInfo error:',
      error?.response?.data || error.message,
    );

    return {
      success: false,
      country: default_country,
      region: null,
      flag: null,
    };
  }
};

/**
 * Social login (Google, Facebook, Apple, etc.)
 * @param {string} provider - The provider name (e.g., 'google', 'facebook', 'apple')
 * @param {string} accessToken - The provider-issued ID token or access token
 * @param {string} country - User’s country (from useCountry hook)
 * @returns {Promise<Object>} { success, token, user }
 */
export const socialLogin = async (
  provider,
  idToken,
  country,
  session_state,
) => {
  try {
    const response = await baseApi.post(`/auth/social-login`, {
      provider,
      idToken,
      country,
      session_state,
    });
    return response.data; // Expected: { success: true, token, user }
  } catch (error) {
    console.error('socialLogin error:', error?.response?.data || error.message);
    throw error;
  }
};

/**
 * Registers a new user with full profile information.
 * @param {Object} userData - User signup data.
 * @param {string} userData.name - Full name of the user.
 * @param {string} userData.email - Email address.
 * @param {string} userData.password - Password.
 * @param {string} [userData.phone_number] - Optional phone number.
 * @param {string} [userData.company_name] - Optional company name.
 * @param {string} [userData.job_title] - Optional job title.
 * @param {string} [userData.state] - Optional state.
 * @param {string} [userData.where_you_heard] - Optional info on how they heard about us.
 * @returns {Promise<Object>} Response containing success flag and user details.
 */
export const signupUser = async ({
  name,
  email,
  password,
  phone_number,
  company_name,
  job_title,
  state,
  where_you_heard,
}) => {
  try {
    const response = await baseApi.post('/auth/signup', {
      name,
      email,
      password,
      phone_number,
      company_name,
      job_title,
      state,
      where_you_heard,
    });
    return response.data; // Expected: { success: true, user }
  } catch (error) {
    console.error('signupUser error:', error?.response?.data || error.message);
    throw error;
  }
};
/**
 * Fetch usage/subscription limits for a user.
 * @endpoint GET /users/{uid}/limits
 * @param {string|number} uid - The user's ID.
 * @returns {Promise<Object>} - { success: true, limits: {...} }
 */
export const getUserLimits = async (uid) => {
  if (!uid) throw new Error('User ID (uid) is required to fetch limits.');

  try {
    const { data } = await chatApi.get(`/users/${uid}/limits`);
    return data; // Expected: { success: true, limits: { chats_used, chats_remaining, plan_tier, ... } }
  } catch (error) {
    console.error(
      'getUserLimits error:',
      error?.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Fetches the currently authenticated user's profile.
 * Requires a valid JWT token in the Authorization header.
 * @returns {Promise<Object>} Response containing success flag and user details.
 */
export const fetchMe = async () => {
  try {
    const response = await baseApi.get('/auth/me'); // token is auto-added via interceptor
    return response.data; // Expected: { success: true, user }
  } catch (error) {
    throw error;
  }
};
// Change password (for logged-in users)
export const changePassword = async (userId, oldPassword, newPassword) => {
  try {
    const { data } = await baseApi.post('/auth/change-password', {
      userId,
      oldPassword,
      newPassword,
    });
    return data; // { success: true, message: "Password updated successfully" }
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      'Failed to change password';
    throw new Error(message);
  }
};
// Edit profile (send only fields you want to update)
export const editProfile = async (payload) => {
  try {
    const { data } = await baseApi.put('/auth/edit-profile', payload);
    return data; // { success: true, message: "Profile updated successfully", user }
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      'Failed to update profile';
    throw new Error(message);
  }
};

// ===== EMAIL OTP API =====
export const sendEmailOtp = async (email) => {
  try {
    const { data } = await baseApi.post('/auth/signup/request-otp', { email });
    return data;
  } catch (error) {
    const message = error.response?.data?.detail || 'Failed to send OTP';

    throw new Error(message);
  }
};

export const resendEmailOtp = async (email) => {
  try {
    const { data } = await baseApi.post('/auth/resend-otp', { email });
    return data;
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Failed to resend OTP';
    throw new Error(message);
  }
};

export const forgotPassword = async (email) => {
  const { data } = await baseApi.post('/auth/forgot-password', { email });
  return data;
};

export const resetPassword = async (email, otp, newPassword) => {
  try {
    const { data } = await baseApi.post('/auth/reset-password', {
      email,
      otp,
      newPassword,
    });
    return data; // { success: true, message: "Password reset successful" }
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      'Failed to reset password';
    throw new Error(message);
  }
};

export const signupVerify = async ({
  email,
  otp,
  name,
  password,
  phone_number,
  company_name,
  job_title,
  state,
  where_you_heard,
  country,
  session_state,
}) => {
  try {
    const response = await baseApi.post('/auth/signup/verify', {
      email,
      otp,
      name,
      password,
      phone_number,
      company_name,
      job_title,
      state,
      where_you_heard,
      country,
      session_state,
    });
    return response.data; // Expected: { success: true, user }
  } catch (error) {
    console.error(
      'signupVerify error:',
      error?.response?.data || error.message,
    );
    throw error;
  }
};

// ============================
// 📦 CHAT API METHODS
// ============================

/**
 * Sends a message to the chatbot.
 * @param {string} message - The message to send.
 * @returns {Promise<Object>}
 */

export const sendMessageToBot = async (
  uid,
  message,
  chat_session_id = '',
  signal,
  include_sources = true,
  sources_limit = 20,
  sources_offset = 0,
) => {
  try {
    const response = await chatApi.post(
      'pg/chat',
      {
        uid,
        chat_session_id,
        message,
        memory_type: 'buffer',
        // memory_max_tokens: 4000,
        memory_max_tokens: 8192,
        include_sources,
        sources_limit,
        sources_offset,
      },
      { signal },
    );
    return response.data;
  } catch (error) {
    // ✅ Swallow intentional aborts
    if (error?.name === 'CanceledError' || error?.name === 'AbortError') {
      return null;
    }

    console.error('sendMessageToBot error:', error);
    throw error;
  }
};

const sanitizeFileNamePart = (value = '') =>
  value
    .trim()
    .split('')
    .filter((char) => char.charCodeAt(0) >= 32)
    .join('')
    .replace(/[<>:"/\\|?*]/g, '')
    .replace(/\s+/g, ' ')
    .slice(0, 120);

/**
 * Export a memory document as a binary file.
 * @endpoint POST /pg/chat/memory/export
 * @param {string} memoryId
 * @param {'pdf'} typeDocument
 * @param {string} preferredBaseName
 */
export const exportMemoryDocument = async (
  memoryId,
  typeDocument = 'pdf',
  preferredBaseName = '',
) => {
  if (!memoryId) throw new Error('memory_id is required');

  const response = await chatApi.post(
    '/pg/chat/memory/export',
    {
      memory_id: memoryId,
      type_document: typeDocument,
    },
    {
      responseType: 'blob',
    },
  );

  const disposition = response.headers['content-disposition'];
  let filename = `memory-export.${typeDocument}`;

  if (disposition) {
    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    const normalMatch = disposition.match(/filename="?([^"]+)"?/i);

    if (utf8Match?.[1]) {
      filename = decodeURIComponent(utf8Match[1]);
    } else if (normalMatch?.[1]) {
      filename = normalMatch[1];
    }
  }

  const safePreferred = sanitizeFileNamePart(preferredBaseName);
  const fallbackName = safePreferred ? `${safePreferred}.${typeDocument}` : '';
  const isGenericDefault = /^memory-export(\.|$)/i.test(filename);

  if (fallbackName && (isGenericDefault || !filename?.trim())) {
    filename = fallbackName;
  }

  const mimeType =
    response.headers['content-type'] || 'application/octet-stream';

  const blob = new Blob([response.data], { type: mimeType });
  saveAs(blob, filename);

  return true;
};
/**
 * Fetch memory summary.
 * @returns {Promise<Object>}
 */

export const getMemorySummary = async (uid, chat_session_id) => {
  try {
    const response = await chatApi.get('pg/chat/memory/summary', {
      params: {
        uid,
        chat_session_id: chat_session_id || '',
      },
    });
    return response.data;
  } catch (error) {
    console.error('getMemorySummary error:', error);
    throw error;
  }
};

/**
 * Search memory by query.
 * @param {string} query
 * @returns {Promise<Object>}
 */
export const searchMemory = async (query) => {
  try {
    const response = await chatApi.get(
      `/memory/search?q=${encodeURIComponent(query)}`,
    );
    return response.data;
  } catch (error) {
    console.error('searchMemory error:', error);
    throw error;
  }
};

/**
 * Clear memory with optional retention of legal knowledge.
 * @param {boolean} keepLegalKnowledge
 * @returns {Promise<Object>}
 */
export const clearMemory = async (keepLegalKnowledge = true) => {
  try {
    const response = await chatApi.post('/memory/clear', {
      keep_legal_knowledge: keepLegalKnowledge,
    });
    return response.data;
  } catch (error) {
    console.error('clearMemory error:', error);
    throw error;
  }
};
/**
 * Clear a specific chatbot session.
 * @param {string} sessionId - The ID of the session to clear.
 * @returns {Promise<Object>}
 */
export const clearSession = async (sessionId) => {
  if (!sessionId) {
    throw new Error('Session ID is required to clear a session.');
  }

  try {
    const response = await chatApi.delete(`/sessions/${sessionId}`);
    return response.data;
  } catch (error) {
    console.error('clearSession error:', error);
    throw error;
  }
};
/**
 * Create or initialize a chatbot session.
 * @param {string} sessionId - Unique identifier for the session.
 * @param {string} [memoryType='buffer'] - Type of memory to use (e.g., 'buffer', 'long_term').
 * @returns {Promise<Object>}
 */
export const createSession = async (sessionId, memoryType = 'buffer') => {
  if (!sessionId) {
    throw new Error('Session ID is required to create a session.');
  }

  try {
    const response = await chatApi.post('/sessions', {
      session_id: sessionId,
      memory_type: memoryType || 'buffer', // ensure default
    });
    return response.data;
  } catch (error) {
    console.error('createSession error:', error);
    throw error;
  }
};

/**
 * Fetch general stats.
 * @returns {Promise<Object>}
 */
export const getStats = async () => {
  try {
    const response = await chatApi.get('/stats');
    return response.data;
  } catch (error) {
    console.error('getStats error:', error);
    throw error;
  }
};

// ============================
// 📦 CONTRACT API METHODS
// ============================

/**
 * Fetch contracts list.
 * @returns {Promise<Object>}
 */
export const getContracts = async (uid) => {
  try {
    const response = await contractApi.get('', {
      params: { uid },
    });
    return response.data;
  } catch (error) {
    console.error('getContracts error:', error);
    throw error;
  }
};

/**
 * Upload a file.
 * @param {File} file - The file object to upload.
 * @param {Function} onProgress - Callback for upload progress.
 * @returns {Promise<Object>}
 */

export const uploadFile = async (uid, file, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await contractApi.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    params: { uid }, // ✅ hardcoded query param
    onUploadProgress: (event) => {
      if (onProgress && event.total) {
        const percent = Math.round((event.loaded * 100) / event.total);
        onProgress(percent);
      }
    },
  });

  return response.data;
};

/**
 * Delete a contract by filename.
 * @param {string} fileName - Name of the file to delete (e.g. "example.pdf").
 * @returns {Promise<Object>}
 */
export const deleteContract = async (uid, fileName) => {
  try {
    const response = await contractApi.delete(`/${fileName}`, {
      params: { uid }, // ✅ add uid as query param
    });
    return response.data;
  } catch (error) {
    console.error(`deleteContract error for ${fileName}:`, error);
    throw error;
  }
};

/**
 * Analyze a contract with a specified type.
 * @param {string} analysisType - One of: "full", "risk", "clauses", "compliance", ".
 * @param {string} contractName - The file name (must exist on backend).
 * @returns {Promise<Object>} - Backend analysis result.
 */
export const analyzeContract = async (
  uid,
  analysisType,
  contractName,
  question,
  signal,
) => {
  try {
    const payload = {
      uid, // ✅ always send
      analysis_type: analysisType,
      contract_name: contractName,
    };

    if (analysisType === 'qa' && question) {
      payload.question = question;
    }

    const response = await contractApi.post('/analyze-in-chat', payload, {
      signal,
    });
    return response.data;
  } catch (error) {
    if (error.name === 'CanceledError') {
      console.log('Request canceled:', error.message);
      return; // no error toast for cancelled requests
    }
    console.error(
      `analyzeContract error [${analysisType}] for ${contractName}:`,
      error?.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Fetch report for a specific contract.
 * @param {string} contractName - The name of the contract file (e.g., "example.pdf").
 * @returns {Promise<Object>} - Backend report data.
 */
export const getContractReport = async (uid, contractName) => {
  try {
    if (!contractName) throw new Error('Contract name is required');

    const response = await contractApi.get(
      `/report/${encodeURIComponent(contractName)}`,
      {
        params: { uid },
      },
    );
    return response.data;
  } catch (error) {
    console.error(
      `getContractReport error for ${contractName}:`,
      error?.response?.data || error.message,
    );
    throw error;
  }
};
// GET single analysis
export const getContractAnalysisById = async (analysisId, uid) => {
  if (!analysisId || !uid) {
    throw new Error('analysisId and uid are required');
  }

  const { data } = await contractApi.get(`/report/analysis/${analysisId}`, {
    params: { uid },
  });

  return data;
};

// PUT update analysis
export const updateContractAnalysis = async ({
  analysisId,
  uid,
  results,
  analysis_type,
  question,
}) => {
  if (!analysisId || !uid) {
    throw new Error('analysisId and uid are required');
  }

  const { data } = await contractApi.put(
    `/report/analysis/${analysisId}`,
    {
      results,
      analysis_type,
      question,
    },
    {
      params: { uid },
    },
  );

  return data;
};

// ============================
// 📚 DEEP LEGAL RESEARCH API
// ============================
/**
 * Fetch deep search reports for a specific user.
 * @endpoint GET /chat/deep_search/user/{uid}/reports
 * @param {number} uid - The user ID whose reports to fetch.
 * @param {Object} options - Optional query parameters.
 * @param {number} [options.limit=50] - Max number of reports to fetch (1–200).
 * @param {number} [options.offset=0] - Pagination offset (≥0).
 * @param {number} [options.preview_chars=300] - Character count for content previews (50–5000).
 * @returns {Promise<Object>} - { total, limit, offset, items: [...] }
 */
export const getDeepSearchReports = async (
  uid,
  { limit = 50, offset = 0, preview_chars = 300 } = {},
) => {
  if (!uid)
    throw new Error('User ID (uid) is required to fetch deep search reports.');

  try {
    const { data } = await chatApi.get(`/chat/deep_search/user/reports`, {
      params: {
        limit,
        offset,
        preview_chars,
      },
    });

    return {
      total: data.total || 0,
      limit: data.limit || limit,
      offset: data.offset || offset,
      items: data.items || [],
    };
  } catch (error) {
    console.error(
      'getDeepSearchReports error:',
      error?.response?.data || error.message,
    );
    throw error;
  }
};
// ============================
// 📚 DEEP SEARCH REPORT API
// ============================

export const getDeepSearchReportById = async (identifier) => {
  if (!identifier) throw new Error('identifier is required');

  const { data } = await chatApi.get(`/chat/deep_search/report/${identifier}`);

  return data; // { title, content_md }
};

export const updateDeepSearchReportById = async (
  identifier,
  { title, content_md },
) => {
  if (!identifier) throw new Error('identifier is required');

  const { data } = await chatApi.put(`/chat/deep_search/report/${identifier}`, {
    title,
    content_md,
  });

  return data;
};

/**
 * Download a completed deep search report as a file.
 * @endpoint GET /chat/deep_search/{research_id}/download
 * @param {string} researchId - The unique research session ID.
 * @returns {Promise<void>} - Triggers browser file download.
 */
export const downloadDeepSearchReport = async (researchId, reportTitle) => {
  if (!researchId)
    throw new Error('researchId is required to download a report.');

  try {
    const response = await chatApi.get(
      `/chat/deep_search/session/${researchId}/download`,
      { responseType: 'blob' },
    );

    // --- Extract filename from Content-Disposition (if available) ---
    const disposition = response.headers['content-disposition'];
    let filename = 'deep_research_report';

    if (disposition && disposition.includes('filename=')) {
      const match = disposition.match(/filename="?([^"]+)"?/);
      if (match?.[1]) {
        filename = match[1];
      }
    } else {
      // Fallback: sanitize title or use researchId
      const safeTitle = (reportTitle || researchId)
        .replace(/[<>:"/\\|?*]+/g, '') // illegal chars
        .replace(/\s+/g, '_')
        .trim();
      filename = `${safeTitle}`;
    }

    // --- Detect file extension from server MIME type ---
    const mimeType =
      response.headers['content-type'] || 'application/octet-stream';

    // If server provides no extension, infer from MIME
    if (!filename.includes('.')) {
      if (mimeType === 'application/pdf') {
        filename += '.pdf';
      } else if (mimeType === 'text/plain') {
        filename += '.txt';
      } else {
        // Fallback — unknown type
        filename += '.bin';
      }
    }

    // --- Create Blob ---
    const blob = new Blob([response.data], { type: mimeType });

    // --- Trigger download ---
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();

    // --- Cleanup ---
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    throw error;
  }
};

/**
 * Initiate a deep legal research request.
 * @param {string} query - The legal question to research.
 * @returns {Promise<Object>} - { research_id }
 */
export const startDeepSearch = async (uid, query) => {
  try {
    const { data } = await chatApi.post('/chat/deep_search', {
      query,
      jurisdiction: 'Federal',
      research_depth: 'comprehensive',
      uid,
    });
    return data;
  } catch (error) {
    console.error(
      'startDeepSearch error:',
      error?.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Check the status of a deep research request.
 * @param {string} researchId - The ID from the initiation request.
 * @returns {Promise<Object>} - e.g. { status: 'completed' | 'pending' }
 */
export const getDeepSearchStatus = async (researchId) => {
  try {
    const { data } = await chatApi.get(
      `/chat/deep_search/${researchId}/status`,
    );
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch the final result of a deep legal research request.
 * @param {string} researchId - The research ID used in the query.
 * @returns {Promise<Object>} - Full result object with legal analysis.
 */
export const getDeepSearchResult = async (researchId) => {
  try {
    const { data } = await chatApi.get(
      `/chat/deep_search/${researchId}/result`,
    );
    return data;
  } catch (error) {
    console.error(
      'getDeepSearchResult error:',
      error?.response?.data || error.message,
    );
    throw error;
  }
};

// ============================
// 📦 SUBSCRIPTIONS & ORDERS API
// ============================

/**
 * Create a subscription for the current user.
 * @param {string} packageId
 * @param {string} nonce
 * @returns {Promise<Object>}
 */
export const createSubscription = async (packageId, nonce) => {
  try {
    const { data } = await subscriptionApi.post('/subscriptions/create', {
      package_id: packageId,
      nonce,
    });

    return data;
  } catch (error) {
    console.error('🔴 Subscription API Error:', error?.response?.data || error);

    let raw = error.response?.data?.message || error.message || '';

    // Try extracting Square’s JSON error
    let extractedDetail = null;

    try {
      const jsonMatch = raw.match(/Body:\s*(\{[\s\S]*\})/);

      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[1]);
        extractedDetail = parsed?.errors?.[0]?.detail;
      }
    } catch (err) {
      console.error('❌ Failed to parse Square error JSON');
    }

    const finalMessage =
      extractedDetail ||
      'Unable to process your payment. Please try again or use a different card.';

    throw new Error(finalMessage);
  }
};

/**
 * Pause a Square subscription by ID.
 * @param {string} subscriptionId - The unique Square subscription ID.
 * @returns {Promise<Object>} Response containing success message or error
 */
export const pauseSubscription = async (subscriptionId) => {
  if (!subscriptionId) throw new Error('Subscription ID is required to pause.');

  try {
    const { data } = await subscriptionApi.post(
      `/subscriptions/pause/${subscriptionId}`,
    );
    return data; // Expected: { success: true, message: "Subscription paused successfully" }
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      'Failed to pause subscription';
    throw new Error(message);
  }
};

/**
 * Resume a Square subscription by ID.
 * @param {string} subscriptionId - The unique Square subscription ID.
 * @returns {Promise<Object>} Response containing success message or error
 */
export const resumeSubscription = async (subscriptionId) => {
  if (!subscriptionId)
    throw new Error('Subscription ID is required to resume.');

  try {
    const { data } = await subscriptionApi.post(
      `/subscriptions/resume/${subscriptionId}`,
    );
    return data; // Expected: { success: true, message: "Subscription resumed successfully" }
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      'Failed to resume subscription';
    throw new Error(message);
  }
};

/**
 * Cancel a Square subscription by ID.
 * @param {string} subscriptionId - The unique Square subscription ID.
 * @returns {Promise<Object>} Response containing success message or error
 */
export const cancelSubscription = async (subscriptionId) => {
  if (!subscriptionId)
    throw new Error('Subscription ID is required to resume.');

  try {
    const { data } = await subscriptionApi.post(
      `/subscriptions/cancel/${subscriptionId}`,
    );
    return data; // Expected: { success: true, message: "Subscription cancelled successfully" }
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      'Failed to cancel subscription';
    throw new Error(message);
  }
};
/**
 * Fetch the current user's subscription.
 * @returns {Promise<Object>} Subscription data
 */
export const getSubscription = async () => {
  try {
    const { data } = await baseApi.get('/subscriptions');
    return data;
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch subscription';
    throw new Error(message);
  }
};

/**
 * Fetch the billing/order history.
 * @returns {Promise<Array>} Orders array
 */
export const getOrders = async () => {
  try {
    const { data } = await baseApi.get('/orders');
    return data; // e.g., [{ id, description, amount, status, date }]
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch orders';
    throw new Error(message);
  }
};

export const streamSimpleMessage = async ({
  uid,
  message,
  signal,
  onData,
  onDone,
  onError,
  chat_session_id = '',
}) => {
  try {
    const res = await fetch(
      `${process.env.REACT_APP_CHAT_BASE_URL}/pg/chat/stream`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({
          uid,
          chat_session_id,
          message,
          memory_type: 'buffer',
          streaming: true,
        }),
        signal,
      },
    );

    if (!res.body) {
      onError(new Error('No stream body'));
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();

    while (true) {
      const { value, done } = await reader.read().catch((err) => {
        if (err.name === 'AbortError') {
          console.log('🔥 Stream aborted cleanly');
          onError(err);
          return { done: true };
        }
        throw err;
      });

      if (done) break;

      onData(decoder.decode(value, { stream: true }));
    }

    reader.releaseLock();
    onDone();
  } catch (err) {
    if (err.name === 'AbortError') {
      console.log('🔥 Stream fetch aborted early');
      return;
    }
    onError(err);
  }
};

/**
 * Start a new chat session.
 * @param {Object} params
 * @param {number} params.uid - User ID
 * @param {string} [params.chat_session_id] - Optional, backend can generate if empty
 * @param {string} [params.title] - Optional title for the chat
 * @param {string} [params.memory_type='buffer'] - Memory type ('buffer', 'summary', etc.)
 * @param {number} [params.memory_max_tokens=4000] - Max tokens for memory
 * @returns {Promise<Object>} - { success, chat_session_id, ... }
 */
export const startChatSession = async ({
  uid,
  chat_session_id = '',
  title = 'New Chat',
  memory_type = 'buffer',
  memory_max_tokens = 8192,
}) => {
  try {
    const { data } = await chatApi.post('/pg/chat/start', {
      uid,
      chat_session_id,
      title,
      memory_type,
      memory_max_tokens,
    });
    return data; // { chat_session_id, ... }
  } catch (error) {
    throw error;
  }
};
/**
 * Rename a chat session by ID.
 * @param {string} sessionId - The ID of the chat session.
 * @param {string} newTitle - The new name/title for the session.
 * @returns {Promise<Object>} - { success: true, message: "Session renamed", session }
 */
export const renameChatSession = async (sessionId, newTitle) => {
  if (!sessionId)
    throw new Error('Session ID is required to rename a session.');
  if (!newTitle) throw new Error('New title is required.');

  try {
    const { data } = await chatApi.put(`/pg/chat/sessions`, {
      chat_session_id: sessionId,
      title: newTitle,
    });
    return data; // { success: true, message: "Renamed successfully", session }
  } catch (error) {
    console.error(
      `renameChatSession error for ${sessionId}:`,
      error?.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Delete a chat session by ID.
 * @param {string} sessionId - The ID of the chat session to delete.
 * @returns {Promise<Object>} - { success: true, message: "Session deleted" }
 */
/**
 * Delete a chat session by ID and user ID.
 * @param {string} sessionId - The ID of the chat session to delete.
 * @param {string} uid - The user ID associated with the session.
 * @returns {Promise<Object>} - { success: true, message: "Session deleted" }
 */
export const deleteChatSession = async (sessionId, uid) => {
  if (!sessionId)
    throw new Error('Session ID is required to delete a session.');
  if (!uid) throw new Error('User ID (uid) is required to delete a session.');

  try {
    const { data } = await chatApi.delete(`/pg/chat/sessions`, {
      data: { chat_session_id: sessionId, uid },
    });
    return data; // { success: true, message: "Deleted successfully" }
  } catch (error) {
    console.error(
      `deleteChatSession error for ${sessionId}:`,
      error?.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Fetch chat history (messages for a given session)
 * @param {Object} params
 * @param {number} params.uid - User ID
 * @param {string} params.chat_session_id - Session ID
 * @param {number} [params.limit=100] - Number of messages to fetch
 * @param {number} [params.offset=0] - Offset for pagination
 */
export const getChatHistory = async ({
  uid = 0,
  chat_session_id,
  limit = 100,
  offset = 0,
  include_sources = true,
  sources_limit = 20,
  sources_offset = 0,
}) => {
  if (!chat_session_id) {
    throw new Error('chat_session_id is required to fetch chat history');
  }

  try {
    const payload = {
      uid,
      chat_session_id,
      limit,
      offset,
      include_sources,
      sources_limit,
      sources_offset,
    };

    const { data } = await chatApi.post('/pg/chat/history', payload);

    return data;
  } catch (error) {
    console.error(
      'getChatHistory error:',
      error?.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Create a share link for a chat session.
 * @param {string} chat_session_id - Chat session ID to share
 * @returns {Promise<Object>} - { share_token, chat_session_id, created_at }
 */
export const createChatShareLink = async (chat_session_id) => {
  if (!chat_session_id) {
    throw new Error('chat_session_id is required to create a share link');
  }

  try {
    const { data } = await chatApi.post('/pg/chat/share', { chat_session_id });
    return data;
  } catch (error) {
    console.error(
      'createChatShareLink error:',
      error?.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Fetch shared chat data by public token.
 * @param {string} token - Public share token
 * @returns {Promise<Object>} - Shared chat payload (read-only)
 */
export const getSharedChatByToken = async (token) => {
  if (!token) throw new Error('Share token is required');

  try {
    const { data } = await chatApi.get(`/pg/chat/share/${token}`);
    return data;
  } catch (error) {
    console.error(
      'getSharedChatByToken error:',
      error?.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Fetch AI-generated chat suggestions based on user's last 10 session titles.
 * Cached per user for 3 days. Returns defaults if no chat history exists.
 * @param {string|number} uid - User ID
 * @returns {Promise<Object>} - { suggestions: string[] }
 */
export const getChatSuggestions = async (uid) => {
  if (!uid) throw new Error('uid is required to fetch chat suggestions');

  try {
    const { data } = await chatApi.get('/pg/chat/suggestions', {
      params: { uid },
    });
    return data;
  } catch (error) {
    console.error(
      'getChatSuggestions error:',
      error?.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Fetch research sources for a chat session.
 * Used primarily AFTER streaming completes.
 * @param {string|number} uid - User ID
 * @param {string} chat_session_id - Chat session ID
 * @returns {Promise<Object>} - { research_sources: [...] }
 */
export const getChatSources = async (uid, chat_session_id) => {
  if (!uid) throw new Error('uid is required to fetch chat sources');
  if (!chat_session_id)
    throw new Error('chat_session_id is required to fetch chat sources');

  try {
    const { data } = await chatApi.get('/pg/chat/sources', {
      params: {
        uid,
        chat_session_id,
      },
    });

    return data; // expected: { research_sources: [...] }
  } catch (error) {
    console.error(
      'getChatSources error:',
      error?.response?.data || error.message,
    );
    throw error;
  }
};

// ============================
// 📦 SPACES API METHODS
// ============================

/**
/**
 * Fetch all spaces for a specific user.
 * @param {string} userId - The ID of the user to fetch spaces for.
 * @returns {Promise<Array>} Array of space objects
 */
export const getSpaces = async (userId) => {
  try {
    const { data } = await chatApi.get('/spaces', {
      params: { uid: userId },
    });
    return data;
  } catch (error) {
    console.error('getSpaces error:', error?.response?.data || error.message);
    throw error;
  }
};

/**
 * Create a new space.
 * @param {string} name - Name of the space.
 * @returns {Promise<Object>} The created space object
 */
// include uid (hardcoded = 1) + description
export const createSpace = async (uid, name, description) => {
  try {
    const { data } = await chatApi.post('/spaces', {
      uid,
      name,
      description,
    });

    return data;
  } catch (error) {
    console.error('createSpace error:', error?.response?.data || error.message);
    throw error;
  }
};
/**
 * Rename a space.
 * @endpoint PATCH /spaces/{space_id}/rename
 */
export const renameSpace = async (spaceId, uid, name, description = '') => {
  if (!spaceId) throw new Error('spaceId is required');
  if (!uid) throw new Error('uid is required');
  if (!name) throw new Error('Space name is required');

  try {
    const { data } = await chatApi.patch(`/spaces/${spaceId}/rename`, {
      uid,
      name,
      description,
    });

    return data; // { success: true, message, space }
  } catch (error) {
    console.error(
      `renameSpace error for space ${spaceId}:`,
      error?.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Add a chat session to a specific space.
 * @param {number|string} spaceId - The ID of the space to add the chat to.
 * @param {string} chat_session_id - The chat session ID to link.
 * @param {number|string} uid - The user ID (owner).
 * @returns {Promise<Object>} - { success: true, message: "Chat added to space" }
 */
export const addChatToSpace = async (spaceId, chat_session_id, uid) => {
  if (!spaceId) throw new Error('spaceId is required');
  if (!chat_session_id) throw new Error('chat_session_id is required');
  if (!uid) throw new Error('uid is required');

  try {
    const { data } = await chatApi.post(`/spaces/chats`, {
      chat_session_id,
      space_id: spaceId,
      uid,
    });

    // ✅ Handle both success and structured error responses
    if (data?.status === 'error') {
      throw new Error(data?.error || 'Failed to add chat to space');
    }

    return data;
  } catch (error) {
    const backendMessage =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error.message ||
      'Unexpected server error';
    console.error('addChatToSpace error:', backendMessage);
    throw new Error(backendMessage);
  }
};

/**
 * Fetch enhanced details for a specific space.
 * @param {string|number} spaceId - ID of the space.
 * @param {string|number} uid - ID of the user.
 * @returns {Promise<Object>} - Enhanced space details.
 */
export const getSpaceDetailsEnhanced = async (spaceId, uid) => {
  if (!spaceId) throw new Error('spaceId is required to fetch details');
  if (!uid) throw new Error('uid is required to fetch space details');

  try {
    const { data } = await chatApi.get(`/spaces/${spaceId}/details-enhanced`, {
      params: { uid },
    });

    return data;
  } catch (error) {
    console.error(
      `getSpaceDetailsEnhanced error for ${spaceId}:`,
      error?.response?.data || error.message,
    );
    throw error;
  }
};
/**
 * Delete a chat session from a specific space.
 * @endpoint DELETE /spaces/{space_id}/chats/{chat_session_id}?uid={uid}
 * @param {string|number} spaceId - The ID of the space.
 * @param {string} chatSessionId - The ID of the chat session to delete.
 * @param {string|number} uid - The user's ID (for verification).
 * @returns {Promise<Object>} { success: true, message: "Chat removed from space" }
 */
export const deleteChatFromSpace = async (spaceId, chatSessionId, uid) => {
  if (!spaceId) throw new Error('spaceId is required.');
  if (!chatSessionId) throw new Error('chat_session_id is required.');
  if (!uid) throw new Error('uid is required.');

  try {
    const { data } = await chatApi.delete(
      `/spaces/${spaceId}/chats/${chatSessionId}`,
      { params: { uid } },
    );
    return data;
  } catch (error) {
    console.error(
      `deleteChatFromSpace error (space ${spaceId}, chat ${chatSessionId}):`,
      error?.response?.data || error.message,
    );
    throw error;
  }
};
/**
 * Add a contract to a specific space.
 * @endpoint POST /spaces/contracts
 * @param {string|number} spaceId - The ID of the space.
 * @param {number} contractId - The ID of the contract.
 * @param {number|string} uid - The user ID.
 * @returns {Promise<Object>} { success: true, message: "Contract added to space" }
 */
export const addContractToSpace = async (spaceId, contractId, uid) => {
  if (!spaceId) throw new Error('spaceId is required');
  if (!contractId) throw new Error('contractId is required');
  if (!uid) throw new Error('uid is required');

  try {
    const { data } = await chatApi.post(`/spaces/contracts`, {
      space_id: spaceId,
      contract_id: contractId,
      uid,
    });

    if (data?.status === 'error') {
      throw new Error(data?.error || 'Failed to add contract to space');
    }

    return data;
  } catch (error) {
    const backendMessage =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error.message ||
      'Unexpected server error';
    console.error('addContractToSpace error:', backendMessage);
    throw new Error(backendMessage);
  }
};

/**
 * Delete a contract (document) from a specific space.
 * @endpoint DELETE /spaces/{space_id}/contracts/{contract_id}?uid={uid}
 * @param {string|number} spaceId - The ID of the space.
 * @param {number} contractId - The ID of the contract to remove.
 * @param {string|number} uid - The user's ID.
 * @returns {Promise<Object>} { success: true, message: "Contract removed from space" }
 */
export const deleteContractFromSpace = async (spaceId, contractId, uid) => {
  if (!spaceId) throw new Error('spaceId is required.');
  if (!contractId) throw new Error('contract_id is required.');
  if (!uid) throw new Error('uid is required.');

  try {
    const { data } = await chatApi.delete(
      `/spaces/${spaceId}/contracts/${contractId}`,
      { params: { uid } },
    );
    return data;
  } catch (error) {
    console.error(
      `deleteContractFromSpace error (space ${spaceId}, contract ${contractId}):`,
      error?.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Fetch chat sessions list.
 * @param {Object} params
 * @param {number} [params.uid] - User ID
 * @param {number} [params.limit=50] - Number of sessions
 * @param {number} [params.offset=0] - Offset for pagination
 * @returns {Promise<Object>} - { sessions: [...], count, limit, offset }
 */
export const getChatSessions = async ({ uid, limit = 100, offset = 0 }) => {
  if (!uid) throw new Error('uid is required to fetch chat sessions');

  try {
    const { data } = await chatApi.get('/pg/chat/sessions', {
      params: { uid, limit, offset },
    });
    return {
      total: data.total,
      limit: data.limit,
      offset: data.offset,
      sessions: data.items || [],
    };
  } catch (error) {
    console.error(
      'getChatSessions error:',
      error?.response?.data || error.message,
    );
    throw error;
  }
};
/**
 * Delete a specific analysis by ID.
 * @param {number} analysisId - The unique ID of the analysis to delete.
 * @param {number} uid - User ID for ownership validation.
 * @returns {Promise<Object>} - { success: true, message: "Analysis deleted" }
 */
export const deleteContractAnalysis = async (analysisId, uid) => {
  if (!analysisId || !uid) {
    throw new Error('analysisId and uid are required to delete analysis');
  }

  try {
    const { data } = await contractApi.delete(`/analysis/${analysisId}`, {
      params: { uid },
    });
    return data;
  } catch (error) {
    console.error(`deleteContractAnalysis error for ${analysisId}:`, error);
    throw error;
  }
};

/**
 * Start a new Q&A chat session for a contract.
 * @param {string} contractName - The contract file name.
 * @param {string} title - Optional session title.
 * @returns {Promise<Object>} - { session_id, ... }
 */
export const startContractChat = async (
  uid,
  contractName,
  title = 'Q&A Session',
) => {
  try {
    const { data } = await chatApi.post('pg/chat/start', {
      uid,

      title,
    });
    return data;
  } catch (error) {
    console.error(
      'startContractChat error:',
      error?.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Ask a question within a contract chat session.
 * @param {number} sessionId - The active session ID.
 * @param {string} contractName - The contract file name.
 * @param {string} question - The user’s question.
 * @returns {Promise<Object>} - { answer, ... }
 */
export const askContractQuestion = async (
  uid,
  sessionId,
  contractName,
  question,
) => {
  try {
    const { data } = await chatApi.post('contracts/pg/analyze-and-chat', {
      chat_session_id: sessionId,
      uid,
      contract_name: contractName,
      question,
      analysis_type: 'qa',
    });
    return data;
  } catch (error) {
    console.error(
      'askContractQuestion error:',
      error?.response?.data || error.message,
    );
    throw error;
  }
};
export const getContractChatHistory = async (uid, sessionId) => {
  try {
    const { data } = await chatApi.post('pg/chat/history', {
      uid,
      chat_session_id: sessionId,
    });

    return data;
  } catch (error) {
    console.error(
      'getContractChatHistory error:',
      error?.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Delete a space by ID.
 * @param {string|number} spaceId - The ID of the space to delete.
 * @returns {Promise<Object>} - { success: true, message: "Space deleted" }
 */
export const deleteSpace = async (spaceId, uid) => {
  if (!spaceId) throw new Error('spaceId is required to delete a space.');
  if (!uid) throw new Error('uid is required to delete a space.');

  try {
    const { data } = await chatApi.delete(`/spaces/${spaceId}?uid=${uid}`);
    return data;
  } catch (error) {
    console.error(
      `deleteSpace error for spaceId: ${spaceId}, uid: ${uid}`,
      error?.response?.data || error.message,
    );
    throw error;
  }
};

export const getContractAnalyses = async (
  uid,
  { limit = 50, offset = 0 } = {},
) => {
  try {
    const { data } = await contractApi.get(`/user/analyses`, {
      params: { limit, offset },
    });
    return data; // { uid, total, items: [...] }
  } catch (error) {
    console.error(
      'getContractAnalyses error:',
      error?.response?.data || error.message,
    );
    throw error;
  }
};
export const getUserStatistics = async (uid) => {
  try {
    const { data } = await chatApi.get(`/users/statistics`);
    return data;
  } catch (error) {
    console.error(
      'getUserStatistics error:',
      error?.response?.data || error.message,
    );
    throw error;
  }
};
export const submitFeedback = async ({ subject, message, email }) => {
  try {
    const token = localStorage.getItem('authToken');

    const payload = {
      subject: subject?.trim(),
      message: message?.trim(),
    };

    // API says:
    // - with bearer token => email optional
    // - without bearer token => email required
    if (email?.trim()) {
      payload.email = email.trim();
    }

    if (!token && !payload.email) {
      throw new Error('Email is required when you are not signed in.');
    }

    const { data } = await baseApi.post('/feedback', payload);
    return data;
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.response?.data?.detail ||
      error.message ||
      'Failed to submit feedback';

    throw new Error(message);
  }
};
